import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { catchError, finalize, of, take, tap } from 'rxjs';
import { IonContent, IonSpinner, IonToggle } from '@ionic/angular/standalone';
import { ModalContainerComponent } from '../../../shared/components/modal-container/modal-container.component';
import { IBreadcrumbs } from '../../../shared/components/breadcrumbs/breadcrumbs.component';
import { SensorService } from '../../../core/services/sensors.service';
import {
  IMentorageConfigDetails,
  IMentorageConfigSnapshot,
  IMentorageConfigValueEntry
} from '../../../shared/models/be/mentorageConfig';
import { ToastService } from '../../../core/services/utils/toast.service';
import { IonicColors } from '../../../shared/enums/ionicColors.enum';
import { IFormChangeEmitter } from '../../../shared/components/form/form.component';
import { FormsModule } from '@angular/forms';

@Component({
  selector: 'app-mentorage-config',
  standalone: true,
  templateUrl: './mentorage-config.page.html',
  styleUrls: ['./mentorage-config.page.scss'],
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    TranslateModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    IonContent,
    IonToggle,
    IonSpinner,
    ModalContainerComponent
  ]
})
export class MentorageConfigPage implements OnInit {
  public breadcrumbsList: IBreadcrumbs[] = [];
  public isDataLoading: boolean = false;
  public isSaving: boolean = false;
  public snapshot: IMentorageConfigSnapshot | null = null;
  public flattenedValues: (IMentorageConfigValueEntry & { type?: string, boolValue?: boolean, originalValue?: any })[] = [];
  public deviceId: string = '';
  public deviceLabel: string = '';
  public formStatus: IFormChangeEmitter = {
    canSaveDraft: false,
    canSaveData: false
  };

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private sensorService: SensorService,
    private toastService: ToastService,
    private translateService: TranslateService
  ) { }

  ngOnInit(): void {
    const routeData = this.route.snapshot.data['data'] || {};
    const navigationState = this.router.getCurrentNavigation()?.extras.state || history.state || {};

    this.deviceId = this.route.snapshot.paramMap.get('deviceId') || '';
    this.deviceLabel = navigationState?.additionalData?.label || '';
    this.breadcrumbsList = routeData.breadcrumbs || [
      {
        icon: 'videocam',
        routerLink: '/wita/devices',
        label: 'DEVICES'
      },
      {
        label: 'MENTORAGE_CONFIG'
      }
    ];

    this.loadMentorageConfig();
  }

  loadMentorageConfig(refreshData: boolean = false): void {
    if (!this.deviceId || this.isDataLoading) {
      return;
    }

    this.isDataLoading = true;
    this.formStatus.canSaveData = false;
    this.sensorService.getMentorageConfig$(this.deviceId).pipe(
      take(1),
      tap((snapshot) => {
        this.snapshot = snapshot;
        this.deviceLabel = snapshot.label || this.deviceLabel;
        this.flattenedValues = this.flattenConfigValues(snapshot.config?.values);
        this.syncBreadcrumbsLabel();
        refreshData && this.toastService.showToast(
          this.translateService.instant('GENERAL.SUCCESS.DATA_REFRESHED'),
          IonicColors.GREEN
        );
      }),
      catchError((err) => {
        refreshData && this.toastService.showToast(
          this.translateService.instant('GENERAL.ERRORS.LOADING_DATA'),
          IonicColors.RED
        );
        this.snapshot = null;
        this.flattenedValues = [];
        return of(null);
      }),
      finalize(() => {
        this.isDataLoading = false;
      })
    ).subscribe();
  }

  onValueChange(): void {
    const hasChanges = this.flattenedValues.some(item => {
      if (item.type === 'boolean') {
        return item.boolValue !== item.originalValue;
      }
      return String(item.value) !== String(item.originalValue);
    });
    this.formStatus.canSaveData = hasChanges;
  }

  saveConfig(): void {
    if (!this.deviceId || this.isSaving) return;

    const payloadConfig = this.unflattenConfigValues(this.flattenedValues);
    if (Object.keys(payloadConfig).length === 0) {
      this.toastService.showToast('Nessun parametro modificato', IonicColors.GREY);
      return;
    }

    this.isSaving = true;

    this.sensorService.updateMentorageConfig$(this.deviceId, { config: payloadConfig }).pipe(
      take(1),
      tap(() => {
        this.toastService.showToast(
          this.translateService.instant('GENERAL.SUCCESS.DATA_SAVED', 'Dati salvati con successo'),
          IonicColors.GREEN
        );
        this.loadMentorageConfig();
      }),
      catchError((err) => {
        this.toastService.showToast(
          this.translateService.instant('GENERAL.ERRORS.SAVING_DATA', 'Errore durante il salvataggio dei dati'),
          IonicColors.RED
        );
        return of(null);
      }),
      finalize(() => {
        this.isSaving = false;
      })
    ).subscribe();
  }

  get config(): IMentorageConfigDetails | null {
    return this.snapshot?.config || null;
  }

  get hasSnapshot(): boolean {
    return !!this.snapshot?.snapshotReceived;
  }

  get hasReadableValues(): boolean {
    return this.flattenedValues.length > 0;
  }

  get configErrorMessage(): string | null {
    if (!this.config) {
      return null;
    }

    if (this.config.status === 'error' && this.config.error) {
      return this.config.error;
    }

    if (this.config.parse_status === 'error' && this.config.parse_error) {
      return this.config.parse_error;
    }

    return this.config.error || this.config.parse_error || null;
  }

  get valuesOmittedMessage(): string | null {
    switch (this.config?.values_omitted_reason) {
      case 'disabled':
        return 'La lettura dei valori è disabilitata lato IDM.';
      case 'file_too_large':
        return 'Il file è troppo grande per essere riportato integralmente.';
      default:
        return null;
    }
  }

  get shortHash(): string {
    const fullHash = this.config?.hash_sha256 || '';
    return fullHash ? fullHash.slice(0, 12) : '--';
  }

  formatDateTime(value?: string | null): string {
    if (!value) {
      return '--';
    }

    const parsedDate = new Date(value);
    if (Number.isNaN(parsedDate.getTime())) {
      return value;
    }

    return parsedDate.toLocaleString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  }

  formatSize(value?: number | null): string {
    if (value === null || value === undefined) {
      return '--';
    }

    return `${value.toLocaleString('it-IT')} B`;
  }

  getStatusBadgeClass(status?: string | null): string {
    switch (status) {
      case 'available':
      case 'ok':
        return 'is-success';
      case 'missing':
        return 'is-warning';
      case 'error':
        return 'is-error';
      default:
        return 'is-neutral';
    }
  }

  private syncBreadcrumbsLabel(): void {
    const lastBreadcrumb = this.breadcrumbsList[this.breadcrumbsList.length - 1];
    if (lastBreadcrumb && this.deviceLabel) {
      lastBreadcrumb.breadCrumbSubject = this.deviceLabel;
      lastBreadcrumb.showBreadCrumbSubject = true;
    }
  }

  private flattenConfigValues(values: unknown, prefix: string = ''): any[] {
    if (values === null || values === undefined) {
      return [];
    }

    if (Array.isArray(values)) {
      return values.flatMap((item, index) => this.flattenConfigValues(item, `${prefix}[${index}]`));
    }

    if (typeof values === 'object') {
      const objectEntries = Object.entries(values as Record<string, unknown>);
      return objectEntries.flatMap(([key, value]) => {
        const nextPrefix = prefix ? `${prefix}.${key}` : key;
        return this.flattenConfigValues(value, nextPrefix);
      });
    }

    const isOnOffStr = values === 'on' || values === 'off';
    const isNumStr = typeof values === 'number' || (typeof values === 'string' && values !== '' && !isNaN(Number(values)));

    return [{
      key: prefix,
      value: String(values),
      type: isOnOffStr ? 'boolean' : isNumStr ? 'number' : 'string',
      boolValue: values === 'on',
      originalValue: isOnOffStr ? (values === 'on') : String(values)
    }];
  }

  private unflattenConfigValues(flattened: any[]): any {
    const result: any = {};
    for (const item of flattened) {
      let isChanged = false;
      let valueToSave: any;

      if (item.type === 'boolean') {
        isChanged = item.boolValue !== item.originalValue;
        valueToSave = item.boolValue ? 'on' : 'off';
      } else {
        isChanged = String(item.value) !== String(item.originalValue);
        valueToSave = item.type === 'number' ? Number(item.value) : item.value;
      }

      if (!isChanged) {
        continue;
      }

      const keys = item.key.split('.');
      let current = result;
      for (let i = 0; i < keys.length; i++) {
        const key = keys[i];
        if (i === keys.length - 1) {
          current[key] = valueToSave;
        } else {
          current[key] = current[key] || {};
          current = current[key];
        }
      }
    }
    return result;
  }
}

